import React, { useState, useEffect } from "react";
import { api } from "../lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Mail, Loader2, Plus, X, Eye, FileText } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_HTML = (prop) => {
  const numero = `${prop.numero}${(prop.versao || 1) > 1 ? ` v${prop.versao}` : ""}`;
  return `<p>Olá, <strong>${prop.cliente_nome || ""}</strong>,</p>
<p>Segue em anexo a proposta nº <strong>${numero}</strong>.</p>
<p>Estamos à disposição para esclarecimentos.</p>
<p>Atenciosamente,<br/>${prop.vendedor_nome || ""}<br/>Locar Ativos Brasil</p>`;
};

export default function EnviarPropostaModal({ proposta, open, onOpenChange, onSent }) {
  const [to, setTo] = useState(proposta?.cliente_email || "");
  const [ccInput, setCcInput] = useState("");
  const [cc, setCc] = useState([]);
  const [assunto, setAssunto] = useState("");
  const [corpo, setCorpo] = useState("");
  const [sending, setSending] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [tab, setTab] = useState("compor");

  React.useEffect(() => {
    if (open && proposta) {
      setTo(proposta.cliente_email || "");
      setCc([]);
      setCcInput("");
      setTab("compor");
      const numero = `${proposta.numero}${(proposta.versao || 1) > 1 ? ` v${proposta.versao}` : ""}`;
      setAssunto(`Proposta Comercial ${numero} — ${proposta.cliente_nome || ""}`);
      setCorpo(DEFAULT_HTML(proposta));
      // Limpa preview anterior
      if (pdfUrl) { window.URL.revokeObjectURL(pdfUrl); }
      setPdfUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposta]);

  useEffect(() => () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const carregarPdf = async () => {
    if (!proposta?.id) return;
    setPdfLoading(true);
    try {
      const resp = await api.get(`/proposta-nativa/${proposta.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao carregar PDF");
    } finally {
      setPdfLoading(false);
    }
  };

  // Quando muda para tab "preview" pela primeira vez, carrega o PDF
  useEffect(() => {
    if (tab === "preview" && !pdfUrl && open) carregarPdf();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, open]);

  const addCc = () => {
    const v = ccInput.trim();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("E-mail inválido");
      return;
    }
    if (cc.includes(v)) return;
    setCc([...cc, v]);
    setCcInput("");
  };

  const removeCc = (e) => setCc(cc.filter((x) => x !== e));

  const enviar = async () => {
    if (!to.trim()) {
      toast.error("Destinatário obrigatório");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/proposta-nativa/${proposta.id}/enviar-custom`, {
        to: to.trim(),
        cc,
        assunto: assunto.trim() || undefined,
        corpo_html: corpo,
      });
      toast.success(`Enviado via ${data.mode || "sendgrid"}${cc.length ? ` (CC: ${cc.length})` : ""}`);
      onSent?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="enviar-proposta-modal">
        <DialogHeader>
          <DialogTitle>Enviar proposta {proposta?.numero}{(proposta?.versao || 1) > 1 ? ` v${proposta.versao}` : ""}</DialogTitle>
          <DialogDescription>Edite o e-mail e veja o preview do PDF antes de enviar ao cliente.</DialogDescription>
        </DialogHeader>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-2">
            <TabsTrigger value="compor" data-testid="env-tab-compor"><Mail size={12} className="mr-1" /> Compor e-mail</TabsTrigger>
            <TabsTrigger value="preview" data-testid="env-tab-preview"><Eye size={12} className="mr-1" /> Preview do PDF</TabsTrigger>
          </TabsList>

          <TabsContent value="compor" className="space-y-3 pt-3">
            <div>
              <Label>Para *</Label>
              <Input data-testid="env-to" value={to} onChange={(e) => setTo(e.target.value)} placeholder="cliente@empresa.com.br" />
            </div>
            <div>
              <Label>CC (opcional)</Label>
              <div className="flex gap-2">
                <Input
                  data-testid="env-cc-input"
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCc(); } }}
                  placeholder="Digite um e-mail e pressione Enter"
                />
                <Button size="sm" variant="outline" onClick={addCc} data-testid="env-cc-add"><Plus size={14} /></Button>
              </div>
              {cc.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {cc.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-200 text-blue-900 text-xs px-2 py-1 rounded" data-testid={`env-cc-tag-${e}`}>
                      {e}
                      <button onClick={() => removeCc(e)} type="button" className="hover:text-red-600"><X size={10} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Assunto</Label>
              <Input data-testid="env-assunto" value={assunto} onChange={(e) => setAssunto(e.target.value)} />
            </div>
            <div>
              <Label>Mensagem (HTML)</Label>
              <Textarea data-testid="env-corpo" rows={8} value={corpo} onChange={(e) => setCorpo(e.target.value)} className="font-mono text-xs" />
              <div className="text-xs text-muted-foreground mt-1">O PDF da proposta será anexado automaticamente.</div>
            </div>
          </TabsContent>

          <TabsContent value="preview" className="pt-3">
            <div className="bg-slate-50 rounded p-2 mb-2 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <FileText size={14} style={{ color: "#1B3A6B" }} />
                Pré-visualização do PDF (proposta + contrato, se houver). Esse é o documento que será anexado.
              </div>
              <Button size="sm" variant="outline" onClick={carregarPdf} disabled={pdfLoading} data-testid="env-preview-refresh">
                {pdfLoading ? <Loader2 size={12} className="mr-1 animate-spin" /> : <Eye size={12} className="mr-1" />}
                Recarregar
              </Button>
            </div>
            {pdfLoading && !pdfUrl ? (
              <div className="h-[60vh] flex items-center justify-center text-muted-foreground">
                <Loader2 size={20} className="animate-spin mr-2" /> Gerando preview...
              </div>
            ) : pdfUrl ? (
              <iframe
                src={pdfUrl}
                title="Preview da proposta"
                className="w-full"
                style={{ height: "65vh", border: "1px solid #e5e7eb", borderRadius: 6 }}
                data-testid="env-preview-iframe"
              />
            ) : (
              <div className="h-[60vh] flex items-center justify-center text-muted-foreground text-sm">
                Clique em "Recarregar" para visualizar.
              </div>
            )}
          </TabsContent>
        </Tabs>

        <div className="flex justify-end gap-2 pt-3 border-t mt-3">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={sending}>Cancelar</Button>
          <Button onClick={enviar} disabled={sending} className="text-white" style={{ background: "#F97316" }} data-testid="env-enviar">
            {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />}
            Enviar agora
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
